'use client';

/**
 * Who this browser is currently signed in as, for the offline layer.
 *
 * The offline queue lives in IndexedDB, which is shared by every tab and every
 * user of the device. Nothing may be read out of it without knowing whose it is,
 * so the scope is held in one place and every offline read and write takes it as
 * an argument.
 *
 * A module-level store rather than React context, for two reasons: the queue is
 * written from plain functions in form handlers, not only from components, and
 * the provider that consumes it sits above the layout that knows the answer.
 */
import * as React from 'react';
import type { SessionScope } from './db';

export type OfflineSession = SessionScope & { userName: string };

let current: OfflineSession | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Set by the dashboard layout on every render, from the server-resolved actor. */
export function setOfflineSession(session: OfflineSession | null): void {
  const changed =
    current?.organizationId !== session?.organizationId ||
    current?.userId !== session?.userId ||
    current?.branchId !== session?.branchId;
  current = session;
  if (changed) emit();
}

export function getOfflineSession(): OfflineSession | null {
  return current;
}

/**
 * The scope to pass to an offline read or write.
 *
 * Returns a scope that matches nothing when there is no session, so a call made
 * before sign-in resolves reads as empty rather than returning another user's
 * rows — failing closed rather than open.
 */
export function offlineScope(): SessionScope {
  return current ?? { organizationId: '', userId: '', branchId: null };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const SERVER_SNAPSHOT: OfflineSession | null = null;

/** React binding, so components re-read the queue when the user changes. */
export function useOfflineSession(): OfflineSession | null {
  return React.useSyncExternalStore(subscribe, getOfflineSession, () => SERVER_SNAPSHOT);
}
